import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LayoutSidebar } from '../../Layout';
import { APIClient } from '../../utils/APIClient';
import { API_URL } from '../../settings/local_settings';
import { withRouterParams } from '../../utils/withRouterParams';
import { ClientTabs } from './ClientTabs';
import { ClientHeader } from './ClientHeader';
import { FaIcon } from '../FaIcon';
import { BudgetHistoryPanel } from '../budget_history/BudgetHistoryPanel';

class ClientBudgetHistoryComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // El nombre sale del propio overview (`client_name`), sin un fetch extra a `client/:id`.
            clientName: '',
            overview: null,
            // historyByBuId mapea business_unit_id → { loading, history, error }
            historyByBuId: {},
            // Conjunto de BU ids expandidas (controlado por el usuario via click)
            expandedBuIds: new Set(),
        };

        this.toggleBu = this.toggleBu.bind(this);
    }

    componentDidMount() {
        this.fetchOverview();
    }

    getClientId() {
        return this.props.match.params.id;
    }

    /**
     * La línea que pidió la URL (`?business_unit=<id>`), o `null`.
     *
     * El Panel del cliente linkea el historial DESDE una fila de línea de control: sin esto, el
     * botón de esa fila abría la pantalla con la PRIMERA línea del cliente expandida, que en un
     * cliente con quince líneas casi nunca es la que se acaba de clickear. El parámetro es una
     * sugerencia, no un filtro: el resto de las líneas sigue estando a la vista.
     */
    requestedBusinessUnitId() {
        const search = (this.props.location && this.props.location.search) || '';
        const raw = new URLSearchParams(search).get('business_unit');
        const id = Number(raw);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    fetchOverview() {
        const url = `${API_URL}agency-client/${this.getClientId()}/budget-history-overview/`;
        new APIClient(url).call('GET').then((response) => {
            if (!response) return;
            if (response.status === 'ERROR') {
                toast.error(response.error_message);
                return;
            }
            if (response.status === 'OK') {
                const overview = response.data;
                this.setState({ overview, clientName: overview.client_name || '' });
                // Auto-expandir un BU (UX: el usuario casi siempre quiere ver alguna data al entrar).
                // Si la URL pidió una línea concreta y esa línea existe en el cliente, gana ella;
                // si no —o si vino un id que no es de este cliente— se cae al primero de la lista.
                const businessUnits = (overview && overview.business_units) || [];
                if (businessUnits.length > 0) {
                    const requested = this.requestedBusinessUnitId();
                    const match = businessUnits.find(
                        (bu) => Number(bu.business_unit_id) === requested);
                    this.expandBu(match ? match.business_unit_id
                        : businessUnits[0].business_unit_id);
                }
            }
        });
    }

    expandBu(buId) {
        this.setState((prev) => {
            const expandedBuIds = new Set(prev.expandedBuIds);
            expandedBuIds.add(buId);
            return { expandedBuIds };
        }, () => this.fetchHistoryForBuIfNeeded(buId));
    }

    collapseBu(buId) {
        this.setState((prev) => {
            const expandedBuIds = new Set(prev.expandedBuIds);
            expandedBuIds.delete(buId);
            return { expandedBuIds };
        });
    }

    toggleBu(buId) {
        if (this.state.expandedBuIds.has(buId)) {
            this.collapseBu(buId);
        } else {
            this.expandBu(buId);
        }
    }

    fetchHistoryForBuIfNeeded(buId) {
        const cached = this.state.historyByBuId[buId];
        if (cached && (cached.history || cached.loading)) return;

        this.setState((prev) => ({
            historyByBuId: {
                ...prev.historyByBuId,
                [buId]: { loading: true, history: null, error: null },
            },
        }));

        const url = `${API_URL}agency-client/${this.getClientId()}/business-unit/${buId}/budget-history/`;
        new APIClient(url).call('GET').then((response) => {
            if (!response) {
                this.setState((prev) => ({
                    historyByBuId: {
                        ...prev.historyByBuId,
                        [buId]: { loading: false, history: null, error: this.props.t('client_budget_history.loading') },
                    },
                }));
                return;
            }
            if (response.status === 'ERROR') {
                this.setState((prev) => ({
                    historyByBuId: {
                        ...prev.historyByBuId,
                        [buId]: { loading: false, history: null, error: response.error_message },
                    },
                }));
                return;
            }
            if (response.status === 'OK') {
                this.setState((prev) => ({
                    historyByBuId: {
                        ...prev.historyByBuId,
                        [buId]: {
                            loading: false,
                            history: response.data && response.data.budget_history ? response.data.budget_history : [],
                            error: null,
                        },
                    },
                }));
            }
        });
    }

    renderBuAccordion(bu) {
        const { t } = this.props;
        const isExpanded = this.state.expandedBuIds.has(bu.business_unit_id);
        const cached = this.state.historyByBuId[bu.business_unit_id];
        const headerClass = `box-header bu-accordion-header${isExpanded ? ' -expanded' : ''}`;

        return (
            <div key={bu.business_unit_id} className="box bu-accordion">
                <header
                    className={headerClass}
                    onClick={() => this.toggleBu(bu.business_unit_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.toggleBu(bu.business_unit_id);
                        }
                    }}
                    aria-expanded={isExpanded}
                >
                    <h3 className="box-title">{bu.business_unit_name}</h3>
                    <span className="bu-accordion-chevron" aria-hidden="true">
                        <FaIcon iconName={isExpanded ? 'chevron-up' : 'chevron-down'} />
                    </span>
                </header>
                {isExpanded && (
                    <div className="bu-accordion-body">
                        {cached && cached.loading && (
                            <div className="budget-history-loading">
                                {t('client_budget_history.loading')}
                            </div>
                        )}
                        {cached && cached.error && (
                            <div className="budget-history-error">{cached.error}</div>
                        )}
                        {cached && cached.history && (
                            <BudgetHistoryPanel budgetHistory={cached.history} />
                        )}
                    </div>
                )}
            </div>
        );
    }

    renderEmpty() {
        const { t } = this.props;
        return (
            <div className="budget-history-empty">
                {t('client_budget_history.no_business_units')}
            </div>
        );
    }

    render() {
        const { t } = this.props;
        const clientId = this.getClientId();
        const overview = this.state.overview;

        return (
            <div className="app-content">
                <LayoutSidebar />
                <section className="section-content">
                    <ClientHeader name={this.state.clientName} />
                    <ClientTabs clientId={clientId} />
                    <div className="section-body h-items">
                        <div className="section-body-item -widest box">
                            <header className="box-header">
                                <h2 className="box-title">{t('client_budget_history.title')}</h2>
                                <small>{t('client_budget_history.subtitle')}</small>
                            </header>
                            <div style={{ padding: '1em' }}>
                                {overview === null && (
                                    <div className="budget-history-loading">{t('client_budget_history.loading')}</div>
                                )}
                                {overview !== null && overview.business_units && overview.business_units.length === 0 && this.renderEmpty()}
                                {overview !== null && overview.business_units && overview.business_units.length > 0 &&
                                    overview.business_units.map((bu) => this.renderBuAccordion(bu))}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default withRouterParams(withTranslation()(ClientBudgetHistoryComponent));
